/**
 * Safely formats a date string or Date object to a localized string
 * @param {string|Date} dateValue - The date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string or fallback text
 */
export const safeFormatDate = (dateValue, options = {}) => {
  if (!dateValue) {
    return "Date not available";
  }

  try {
    const date = new Date(dateValue);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    const defaultOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...options,
    };

    return date.toLocaleString(undefined, defaultOptions);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Date formatting error";
  }
};

/**
 * Safely formats a date to show only the date part
 * @param {string|Date} dateValue - The date to format
 * @returns {string} Formatted date string or fallback text
 */
export const safeFormatDateOnly = (dateValue) => {
  return safeFormatDate(dateValue, {
    hour: undefined,
    minute: undefined,
  });
};

/**
 * Safely formats a date to show both date and time clearly
 * @param {string|Date} dateValue - The date to format
 * @returns {string} Formatted datetime string or fallback text
 */
export const safeFormatDateTime = (dateValue) => {
  if (!dateValue) {
    return "Date not available";
  }

  try {
    const date = new Date(dateValue);

    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${dateStr} at ${timeStr}`;
  } catch (error) {
    console.error("Error formatting date time:", error);
    return "Date formatting error";
  }
};
